import { useQuery, UseQueryResult } from 'react-query';
import useFeatureFlags from '../../../app/src/hooks/useFeatureFlags';
import { request } from '../legacy/services/httpSessionService';
import { AffiliationInfo } from '../../../../types/Broker';
import { useBrokerAffiliations } from '../../../sales-conversation/src/components/selection/SelectionQueries';
import { isSocialLoginUrl } from '../../../app/src/features/authentication/constants/SocialAuth';

// Constants

const PIA = {
  insurances: ['car'],
  label: 'PIA',
  logo: 'https://files.wegroup.be/logos/insurances/pia.png',
  name: 'pia',
  needs_verification: false,
  needs_access_request: false,
  required_integrations: [],
  required_nrs: ['nr'],
  can_save: [],
};
export interface Affiliations {
  items: AffiliationInfo[];
}

export const getAffiliations = async (): Promise<[Affiliations, number]> => {
  const path = `v1/api/affiliations`;
  const [resp, status] = await request({ path });

  return [resp, status];
};

export const useAffiliations = (): UseQueryResult<Affiliations, unknown> => {
  const { isPiaEnabled } = useFeatureFlags();

  return useQuery<Affiliations>(
    ['affiliationsREST'],
    async () => {
      const [resp, status] = await getAffiliations();
      if (status !== 200) throw new Error();

      if (isPiaEnabled && resp.items) resp.items.push(PIA);

      return resp;
    },
    {
      refetchOnWindowFocus: false,
      enabled: !isSocialLoginUrl,
    },
  );
};

interface AvailableAffiliations {
  isLoading: boolean;
  data?: AffiliationInfo[];
}

/**
 * This hook returns a list of insurances that the broker has connected to his account.
 */
export const useAvailableAffiliations = (): AvailableAffiliations => {
  const { data: brokerAffiliations, isLoading: isLoadingBrokerAff } =
    useBrokerAffiliations();
  const { data: allAffiliations, isLoading: isLoadingAff } = useAffiliations();

  if (
    brokerAffiliations &&
    allAffiliations &&
    !isLoadingBrokerAff &&
    !isLoadingAff
  ) {
    const availableAffiliations = allAffiliations.items.filter(({ name }) =>
      brokerAffiliations?.items.some(
        ({ affiliation }) => name === affiliation.name,
      ),
    );

    return {
      data: availableAffiliations,
      isLoading: false,
    };
  }

  return {
    data: undefined,
    isLoading: isLoadingBrokerAff || isLoadingAff,
  };
};
